import Service, { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';

import type StoreService from '@ember-data/store';
import type GroupModel from 'later/models/group';
import type AuthService from 'later/services/auth';
import type OnboardingService from 'later/services/onboarding';

/**
 * Centralized logic for determining if the current user is in the "creator cohort".
 * Used to:
 * - Redirect creator users to Creator Home from the index route
 * - Suppress plans/trial pop-ups on Calendar (and elsewhere) for creator cohort
 *
 * Creator cohort is defined as users who:
 * 1. Signed up through Later Creator flow (utm_source='creator' or feature_source='creator' or 'mavrck')
 * 2. Signed up through Mavely flow (utm_source='mavely' or feature_source='mavely')
 * 3. Have an influencer record (hasCreatorProfile or any social identity in a user's groups or store has influencerId)
 */
export default class CreatorCohortService extends Service {
  @service declare auth: AuthService;
  @service declare onboarding: OnboardingService;
  @service declare store: StoreService;

  /**
   * Returns true if the current user is in the creator cohort.
   * Pass an optional group when the context has a specific group (e.g. index redirect);
   * otherwise the service uses auth.lastGroup and store for resolution.
   */
  isCreatorCohort(group?: GroupModel): boolean {
    return this.isCreatorOrMavelySignup() || this.hasInfluencerRecord(group);
  }

  /**
   * Checks if user signed up through Later Creator or Mavely flow
   */
  isCreatorOrMavelySignup(): boolean {
    const creatorSources = ['creator', 'mavrck', 'mavely'];
    const urlParams = new URLSearchParams(window.location.search);

    return [
      urlParams.get('utm_source')?.toLowerCase(),
      urlParams.get('feature_source')?.toLowerCase(),
      this.onboarding.signupSource?.toLowerCase(),
      this.auth.currentUserModel?.signupSource?.toLowerCase()
    ].some((source) => source && creatorSources.includes(source));
  }

  /**
   * Checks if user has an influencer record.
   * This can be determined by:
   * 1. Account has creator profile (hasCreatorProfile)
   * 2. Any social identity in any given group has an influencerId
   */
  hasInfluencerRecord(group?: GroupModel): boolean {
    const { hasCreatorProfile } = this.auth.currentAccount || {};
    const groups = [group, ...(this.store?.peekAll('group')?.toArray() ?? [])].filter(isPresent);
    const allSocialIdentities = this.store.peekAll('social-identity').toArray();

    return !!(
      hasCreatorProfile ||
      groups.some(
        (g) =>
          g?.socialIdentities.toArray().some((si) => isPresent(si?.influencerId)) ||
          allSocialIdentities.some((si) => si.belongsTo('group').id() === g?.id && isPresent(si.influencerId))
      )
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    'creator-cohort': CreatorCohortService;
  }
}
